import React, { useState, useEffect } from 'react';
import { LineChart, Line, ResponsiveContainer } from 'recharts';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import api, { type DebtMetric } from '../api';

interface MetricCardProps {
  label: string;
  hint?: string; // plain-language explanation (hover tooltip) for non-technical users
  current: number;
  previous?: number;
  data: number[];
  color: string;
  invertTrend?: boolean; // true = higher is worse (default), false = higher is better
}

function getTrend(current: number, previous: number | undefined, invertTrend: boolean): {
  icon: React.ReactNode;
  label: string;
  color: string;
} {
  if (previous === undefined || previous === current) {
    return { icon: <Minus size={12} />, label: 'Stable', color: 'var(--text-muted)' };
  }
  const increased = current > previous;
  const isGood = invertTrend ? !increased : increased;
  return {
    icon: increased ? <TrendingUp size={12} /> : <TrendingDown size={12} />,
    label: isGood ? 'Improving' : 'Degrading',
    color: isGood ? 'var(--color-success, #22c55e)' : 'var(--color-error, #ef4444)',
  };
}

const MetricCard: React.FC<MetricCardProps> = ({ label, hint, current, previous, data, color, invertTrend = true }) => {
  const trend = getTrend(current, previous, invertTrend);
  const delta = previous !== undefined ? current - previous : 0;
  const deltaStr = delta > 0 ? `+${delta}` : String(delta);

  return (
    <div title={hint} style={{
      background: 'var(--bg-card, var(--bg))',
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius-md, 8px)',
      padding: '12px 14px',
      minWidth: 0,
    }}>
      <div style={{ fontSize: 'var(--text-xs, 11px)', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {label}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <span style={{ fontSize: 'var(--text-xl, 20px)', fontWeight: 700, color: 'var(--text)' }}>
          {current.toLocaleString()}
        </span>
        {previous !== undefined && delta !== 0 && (
          <span style={{ fontSize: 'var(--text-xs, 11px)', color: trend.color, display: 'flex', alignItems: 'center', gap: 2 }}>
            {trend.icon} {deltaStr}
          </span>
        )}
      </div>
      {data.length > 1 && (
        <div style={{ height: 32, width: '100%' }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data.map((v, i) => ({ v, i }))}>
              <Line type="monotone" dataKey="v" stroke={color} strokeWidth={1.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
      <div style={{ fontSize: 'var(--text-xs, 10px)', color: trend.color, marginTop: 4 }}>
        {trend.label}
      </div>
    </div>
  );
};

interface CodeHealthProps {
  projectId: string;
}

const CodeHealth: React.FC<CodeHealthProps> = ({ projectId }) => {
  const [metrics, setMetrics] = useState<DebtMetric[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api.debtTrend(projectId);
        if (!cancelled) setMetrics(data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  if (loading) return null;
  if (metrics.length === 0) return null;

  // Metrics are returned newest-first; reverse for sparkline (oldest → newest)
  const chronological = [...metrics].reverse();
  const latest = metrics[0];
  const previous = metrics.length > 1 ? metrics[1] : undefined;

  return (
    <div style={{ marginBottom: 16 }}>
      <div className="section-label" style={{ marginBottom: 8 }}>Code Health</div>
      <div style={{
        display: 'grid',
        // Force a single row of 4 (minmax(0,1fr) lets cards shrink instead of wrapping).
        gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
        gap: 8,
      }}>
        <MetricCard
          label="Loose types"
          hint="Values typed as `any` in TypeScript — weaker type safety, easier for bugs to slip through. Lower is better."
          current={latest.any_count}
          previous={previous?.any_count}
          data={chronological.map(m => m.any_count)}
          color="#f59e0b"
        />
        <MetricCard
          label="Hidden errors"
          hint="Empty catch blocks that swallow errors instead of handling them. Lower is better."
          current={latest.empty_catch_count}
          previous={previous?.empty_catch_count}
          data={chronological.map(m => m.empty_catch_count)}
          color="#ef4444"
        />
        <MetricCard
          label="Critical security"
          hint="Critical (P0) security findings from static analysis (SAST). Lower is better."
          current={latest.sast_p0_count}
          previous={previous?.sast_p0_count}
          data={chronological.map(m => m.sast_p0_count)}
          color="#dc2626"
        />
        <MetricCard
          label="Tests passing"
          hint="Number of automated tests currently passing. Higher is better."
          current={latest.test_count}
          previous={previous?.test_count}
          data={chronological.map(m => m.test_count)}
          color="#22c55e"
          invertTrend={false}
        />
      </div>
    </div>
  );
};

export default CodeHealth;
